import { LEGACY_FIELDS } from './legacy-preset';
import { SYSTEM_FIELDS } from './system-fields';
import type { CompiledField, CompiledParameters, LegacyParameters, ParameterFields, ParameterValue } from './parameter-types';

// 继续禁止宿主将已移除的内部标记重新登记为接口字段或别名。
const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor', 'merCodeEmpty', '_randomTemp']);

function isRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  // CDN/iframe 的 Object.prototype 引用不同，不能用当前 realm 的对象身份判断。
  return prototype === null || (Object.getPrototypeOf(prototype) === null
    && Object.prototype.toString.call(value) === '[object Object]');
}

function assertName(name: unknown): asserts name is string {
  if (typeof name !== 'string' || !name.trim() || FORBIDDEN_KEYS.has(name)) {
    throw new Error('Invalid parameter name');
  }
}

/** 初始化时快照普通 JSON 默认值，避免重试期间被外部对象改写。 */
function snapshotValue(value: unknown, ancestors = new Set<object>(), depth = 0): ParameterValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (depth > 32 || typeof value !== 'object' || !value || ancestors.has(value)) {
    throw new Error('Invalid parameter default');
  }
  ancestors.add(value);
  try {
    if (Array.isArray(value)) return Object.freeze(Array.from(value, (item) => snapshotValue(item, ancestors, depth + 1)));
    if (!isRecord(value)) throw new Error('Invalid parameter object');
    const result: Record<string, ParameterValue> = Object.create(null);
    for (const [key, item] of Object.entries(value)) {
      if (['__proto__', 'prototype', 'constructor'].includes(key)) throw new Error('Unsafe default key');
      result[key] = snapshotValue(item, ancestors, depth + 1);
    }
    return Object.freeze(result);
  } finally {
    ancestors.delete(value);
  }
}

function compileFields(fields: ParameterFields, occupied = new Set<string>()): CompiledField[] {
  if (!isRecord(fields)) throw new Error('Invalid parameter fields');
  const entries = Object.entries(fields);
  // 先登记全部标准名，避免别名冲突的判定依赖配置顺序。
  for (const [name] of entries) {
    assertName(name);
    if (occupied.has(name)) throw new Error('Conflicting parameter');
    occupied.add(name);
  }
  return entries.map(([name, rule]) => {
    if (!isRecord(rule) || Object.keys(rule).some((key) => key !== 'aliases' && key !== 'defaultValue')) {
      throw new Error('Invalid parameter rule');
    }
    const aliases = rule.aliases === undefined ? [] : rule.aliases;
    if (!Array.isArray(aliases)) throw new Error('Invalid parameter aliases');
    const sources = [name];
    for (const alias of aliases) {
      assertName(alias);
      if (occupied.has(alias)) throw new Error('Conflicting parameter alias');
      occupied.add(alias);
      sources.push(alias);
    }
    const defaultValue = rule.defaultValue === undefined ? undefined : snapshotValue(rule.defaultValue);
    return Object.freeze({ name, sources: Object.freeze(sources), defaultValue });
  });
}

const LEGACY: CompiledParameters = Object.freeze({ legacy: true, fields: Object.freeze(compileFields(LEGACY_FIELDS)) });
const SYSTEM: CompiledParameters = Object.freeze({ legacy: false, fields: Object.freeze(compileFields(SYSTEM_FIELDS)) });

/** 非法配置关闭业务输出；诊断不包含宿主值，日志异常也不能影响业务。 */
export function compileParameters(parameters?: LegacyParameters): CompiledParameters {
  try {
    if (parameters === undefined) return LEGACY;
    if (!isRecord(parameters)) throw new Error('Invalid parameter options');
    if (Object.keys(parameters).some((key) => key !== 'mode' && key !== 'fields')) throw new Error('Unknown parameter option');
    if (parameters.mode === 'legacy' && parameters.fields === undefined) return LEGACY;
    if (parameters.mode !== 'configured') throw new Error('Invalid parameter mode');
    const occupied = new Set<string>();
    for (const field of SYSTEM.fields) {
      for (const source of field.sources) occupied.add(source);
    }
    const fields = compileFields(parameters.fields === undefined ? {} : parameters.fields, occupied);
    return Object.freeze({ legacy: false, fields: Object.freeze([...SYSTEM.fields, ...fields]) });
  } catch {
    try { console.warn('[HSS-TRACK:WARN] 参数配置无效，已禁用业务字段，仅保留系统字段'); } catch { /* 日志失败不外溢。 */ }
    return SYSTEM;
  }
}
